import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShoppingCart, Phone, Package, FileText } from "lucide-react";
import EnquiryDialog from "@/components/EnquiryDialog";
import { toast } from "sonner";
import { useLabels } from "@/lib/labels";

// Button text scales with the card width (container query units) and wraps instead of overflowing.
const BTN = "w-full mt-3 h-auto min-h-10 py-2 px-2 whitespace-normal leading-tight text-center font-semibold text-[clamp(0.68rem,5.2cqi,0.875rem)]";

export default function ProductCard({ product }) {
  const { add } = useCart();
  const navigate = useNavigate();
  const [enquiry, setEnquiry] = useState(false);
  const available = product.available;
  const L = useLabels();

  return (
    <div data-testid={`product-card-${product.sku}`}
      className="group rounded-xl border bg-white overflow-hidden flex flex-col min-w-0 [container-type:inline-size] hover:shadow-lg hover:-translate-y-1 transition-[box-shadow,transform] duration-300">
      <button onClick={() => navigate(`/product/${product.slug || product.id}`)} className="relative aspect-square bg-slate-100 overflow-hidden">
        {product.image_url ? (
          <img src={product.image_url}
            alt={`${product.brand && !product.name.toLowerCase().startsWith(product.brand.toLowerCase()) ? product.brand + " " : ""}${product.name}${product.category ? " – " + product.category : ""} | ACP Online`}
            title={`${product.brand && !product.name.toLowerCase().startsWith(product.brand.toLowerCase()) ? product.brand + " " : ""}${product.name}`}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={(e) => { e.currentTarget.style.display = "none"; }} />
        ) : (
          <div className="w-full h-full grid place-items-center text-slate-300">
            <Package size={56} strokeWidth={1.5} />
          </div>
        )}
        <div className="absolute top-3 left-3">
          {product.stock > 0
            ? <Badge className="bg-emerald-600 hover:bg-emerald-600 max-w-[calc(100cqi-1.5rem)] truncate">{L.in_stock_badge}</Badge>
            : product.availability?.status === "lead_time"
            ? <Badge className="bg-amber-500 text-steel hover:bg-amber-500 max-w-[calc(100cqi-1.5rem)] truncate" data-testid={`card-available-to-order-${product.sku}`}>Available to order</Badge>
            : <Badge className="bg-slate-600 hover:bg-slate-600 max-w-[calc(100cqi-1.5rem)] truncate">{L.out_of_stock_badge}</Badge>}
        </div>
      </button>
      <div className="p-3 sm:p-4 flex flex-col flex-1">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground truncate">{product.category}</div>
          {product.brand && (
            <button data-testid={`card-brand-${product.sku}`}
              onClick={(e) => { e.stopPropagation(); navigate(`/brand/${encodeURIComponent(product.brand)}`); }}
              className="shrink-0 text-[11px] font-semibold text-steel hover:text-accent transition-colors">
              {product.brand}
            </button>
          )}
        </div>
        <button onClick={() => navigate(`/product/${product.slug || product.id}`)}
          className="mt-1 text-left font-semibold leading-snug hover:text-accent transition-colors line-clamp-2 break-words [overflow-wrap:anywhere] text-[clamp(0.8rem,6cqi,1rem)]">
          {product.name}
        </button>
        <div className="mt-1 text-xs font-mono text-muted-foreground truncate">{product.sku}</div>
        <div className="mt-auto pt-3">
          <div className="flex items-baseline gap-1.5 flex-wrap">
            {product.poa ? (
              <span className="font-bold text-steel text-[clamp(0.85rem,7cqi,1.125rem)]">{L.poa_price}</span>
            ) : (
              <>
                <span className="font-extrabold font-mono text-accent text-[clamp(1rem,10cqi,1.5rem)]">{money(product.price)}</span>
                <span className="text-[11px] text-muted-foreground">{L.incl_gst}</span>
              </>
            )}
          </div>
          {!product.poa && product.on_special ? (
            <div className="mt-0.5 flex flex-wrap items-baseline gap-x-2" data-testid={`saving-${product.sku}`}>
              <span className="text-sm line-through text-muted-foreground font-mono">{money(product.list_price)}</span>
              <span className="text-xs font-bold text-red-600 uppercase tracking-wide">{L.save_prefix} {money(product.saving)}</span>
            </div>
          ) : product.rrp ? (
            <div className="mt-0.5 flex flex-wrap items-baseline gap-x-2" data-testid={`rrp-saving-${product.sku}`}>
              <span className="text-sm line-through text-muted-foreground font-mono">{L.rrp_prefix} {money(product.rrp)}</span>
              <span className="text-xs font-bold text-red-600 uppercase tracking-wide">{L.save_prefix} {money(product.rrp_saving)}</span>
            </div>
          ) : null}
          {product.poa || product.availability?.action === "quote" ? (
            <Button data-testid={`request-quote-${product.sku}`} onClick={() => setEnquiry(true)}
              className={`${BTN} bg-accent text-steel hover:bg-amber-600`}>
              <FileText size={16} className="mr-1.5 shrink-0" /> <span className="min-w-0">{L.request_quote}</span>
            </Button>
          ) : available ? (
            <Button data-testid={`add-to-cart-${product.sku}`}
              onClick={() => { add(product, 1); toast.success(`${product.name} added to cart`); }}
              className={`${BTN} bg-steel hover:bg-steel-light text-white`}>
              <ShoppingCart size={16} className="mr-1.5 shrink-0" /> <span className="min-w-0">{L.add_to_cart}</span>
            </Button>
          ) : (
            <Button data-testid={`enquire-${product.sku}`} onClick={() => setEnquiry(true)}
              variant="outline" className={`${BTN} border-slate-400`}>
              <Phone size={16} className="mr-1.5 shrink-0" />
              <span className="sm:hidden min-w-0">{L.enquire_button_mobile}</span>
              <span className="hidden sm:inline min-w-0">{L.enquire_button}</span>
            </Button>
          )}
        </div>
      </div>
      <EnquiryDialog product={product} quote={product.poa || product.availability?.action === "quote"} open={enquiry} onOpenChange={setEnquiry} />
    </div>
  );
}
